import React, { useCallback, useEffect, useMemo, useState } from 'react';

import HomeV2 from '../HomeV2/HomeV2';
import { CITY_STORAGE_KEY } from '../../config/launchAccess';
import { DEFAULT_MARKETS, findMarket } from '../../config/launchMarkets';
import PreLaunch from './PreLaunch';
import './PreLaunch.css';

function normalizeMarketResponse(payload) {
  const markets = Array.isArray(payload?.markets) && payload.markets.length
    ? payload.markets
    : DEFAULT_MARKETS;
  const selectedMarket = payload?.selectedMarket
    || findMarket(markets, payload?.selectedMarketSlug)
    || null;

  return {
    markets,
    selectedMarket,
    detectedCity: payload?.detectedCity || '',
  };
}

export default function CityLaunchGate() {
  const [launchData, setLaunchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(() => {
    try {
      return window.localStorage.getItem(CITY_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const loadLaunchStatus = useCallback(async (citySlug = '') => {
    setLoading(true);
    try {
      const query = citySlug ? `?city=${encodeURIComponent(citySlug)}` : '';
      const response = await fetch(`/api/launch-status${query}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Launch status is unavailable');
      const nextData = normalizeMarketResponse(await response.json());
      setLaunchData(nextData);
      if (!citySlug && nextData.selectedMarket?.slug) {
        setSelectedSlug(nextData.selectedMarket.slug);
      }
    } catch {
      const selectedMarket = findMarket(DEFAULT_MARKETS, citySlug);
      setLaunchData({
        markets: DEFAULT_MARKETS,
        selectedMarket,
        detectedCity: '',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLaunchStatus(selectedSlug);
  }, []); // Resolve once on entry; subsequent changes are handled explicitly.

  const handleCityChange = useCallback((citySlug) => {
    setSelectedSlug(citySlug);
    try {
      if (citySlug) window.localStorage.setItem(CITY_STORAGE_KEY, citySlug);
      else window.localStorage.removeItem(CITY_STORAGE_KEY);
    } catch {
      // Storage can be unavailable in strict privacy modes; the gate still works.
    }
    loadLaunchStatus(citySlug);
  }, [loadLaunchStatus]);

  const markets = launchData?.markets || DEFAULT_MARKETS;
  const selectedMarket = useMemo(
    () => findMarket(markets, selectedSlug) || launchData?.selectedMarket || null,
    [launchData?.selectedMarket, markets, selectedSlug]
  );

  if (loading && !launchData) {
    return (
      <main className="prelaunch-loading" aria-busy="true" aria-label="Checking city availability">
        <span className="prelaunch-loading__heart" aria-hidden="true">♥</span>
        <p>Finding your Hebrew Hearts community…</p>
      </main>
    );
  }

  if (selectedMarket?.status === 'launched') {
    return (
      <main>
        <div className="launch-city-bar">
          <div>
            <span className="launch-city-bar__pulse" aria-hidden="true" />
            Hebrew Hearts is live in <strong>{selectedMarket.name}</strong>
          </div>
          <label>
            <span className="sr-only">Change your city</span>
            <select
              aria-label="Change your city"
              value={selectedMarket.slug}
              onChange={(event) => handleCityChange(event.target.value)}
            >
              {markets.map((market) => (
                <option key={market.slug} value={market.slug}>
                  {market.name} — {market.status === 'launched' ? 'Live' : 'Pre-launch'}
                </option>
              ))}
            </select>
          </label>
        </div>
        <HomeV2 />
      </main>
    );
  }

  return (
    <PreLaunch
      markets={markets}
      selectedMarket={selectedMarket}
      detectedCity={launchData?.detectedCity}
      selectedSlug={selectedSlug}
      onCityChange={handleCityChange}
      launchStatusLoading={loading}
    />
  );
}
